import {
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  Stack,
  TextField,
} from '@mui/material';
import React, { useState } from 'react';
import { useCreateMunicipality } from '../hooks';
import { useAlert } from '../../context/AlertContext';
import type { IMunicipality } from '../../Interfaces/IMunicipality';
import type { IFederalState } from '../../Interfaces/IAemter';

interface IProps {
  municipalities: IMunicipality[];
  federalStates: IFederalState[];
  onClose: () => void;
  onCreated: () => void;
}

/** Neue politische Gemeinde (#1207) – Name + Bundesland + optionaler AGS. */
export const AddMunicipalityDialog: React.FC<IProps> = ({
  municipalities,
  federalStates,
  onClose,
  onCreated,
}) => {
  const { showAlert } = useAlert();
  const { createMunicipality, isPendingCreateMunicipality } =
    useCreateMunicipality();

  const [name, setName] = useState('');
  const [idFederalState, setIdFederalState] = useState<number | ''>('');
  const [ags, setAgs] = useState('');

  const isDuplicate = municipalities.some(
    m =>
      m.Municipality.trim().toLowerCase() === name.trim().toLowerCase() &&
      m.idFederalState === idFederalState
  );
  const canSave =
    name.trim().length > 0 &&
    typeof idFederalState === 'number' &&
    !isDuplicate &&
    !isPendingCreateMunicipality;

  const handleSave = async () => {
    if (!canSave || typeof idFederalState !== 'number') return;
    try {
      await createMunicipality({
        idMunicipality: -1,
        Municipality: name.trim(),
        idFederalState,
        FederalState:
          federalStates.find(f => f.idFederalState === idFederalState)
            ?.FederalState ?? '',
        AGS: ags.trim() || null,
        activeMunicipality: true,
      });
      showAlert({ text: 'Gemeinde angelegt' });
      onCreated();
      onClose();
    } catch (e) {
      console.error(e);
      showAlert({ severity: 'error', text: 'Fehler beim Speichern' });
    }
  };

  return (
    <Dialog open onClose={onClose} maxWidth='sm' fullWidth>
      <DialogTitle>Neue politische Gemeinde</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <TextField
            label='Name der Gemeinde'
            value={name}
            onChange={e => setName(e.target.value)}
            error={isDuplicate}
            helperText={
              isDuplicate
                ? 'Diese Gemeinde existiert in diesem Bundesland bereits.'
                : undefined
            }
            autoFocus
            fullWidth
          />
          <TextField
            select
            label='Bundesland'
            value={idFederalState === '' ? '' : String(idFederalState)}
            onChange={e => setIdFederalState(Number(e.target.value))}
            fullWidth
          >
            {federalStates
              .slice()
              .sort((a, b) => a.FederalState.localeCompare(b.FederalState))
              .map(fs => (
                <MenuItem key={fs.idFederalState} value={fs.idFederalState}>
                  {fs.FederalState}
                </MenuItem>
              ))}
          </TextField>
          <TextField
            label='Amtlicher Gemeindeschlüssel (AGS, optional)'
            value={ags}
            onChange={e => setAgs(e.target.value)}
            fullWidth
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Abbruch</Button>
        <Button
          variant='contained'
          onClick={() => void handleSave()}
          disabled={!canSave}
        >
          {isPendingCreateMunicipality ? (
            <CircularProgress size={18} />
          ) : (
            'Anlegen'
          )}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
